<template>
  <b-modal
    v-model="visible"
    title="Collect Payment"
    size="lg"
    hide-footer
    no-close-on-backdrop
    @hidden="cleanup"
  >
    <template #modal-title>
      <span><i class="fa fa-cc-stripe mr-1"></i> Collect Payment</span>
    </template>

    <!-- Success State -->
    <div v-if="paymentSuccess" class="text-center py-4">
      <div class="success-checkmark mx-auto mb-3">&#x2713;</div>
      <h4 class="mt-3">Payment Successful!</h4>
      <p class="paid-amount">${{ formatAmount(amount) }}</p>
      <p class="text-muted">{{ patientName }}</p>
    </div>

    <!-- Payment Form -->
    <div v-else>
      <p class="mb-1"><strong>Patient:</strong> {{ patientName }}</p>
      <p class="text-muted mb-3">Balance: ${{ patientBalance.toFixed(2) }}</p>

      <!-- Editable amount -->
      <div class="form-group">
        <label for="stripe-amount">Amount ($)</label>
        <input
          id="stripe-amount"
          type="number"
          v-model.number="amount"
          class="form-control"
          step="0.01"
          min="0.01"
          :max="patientBalance"
          :disabled="processing || cardLocked"
        />
        <small class="text-muted">Max: ${{ patientBalance.toFixed(2) }}</small>
      </div>

      <!-- Optional note -->
      <div class="form-group">
        <label for="stripe-note">Note (optional)</label>
        <textarea
          id="stripe-note"
          v-model="note"
          class="form-control"
          rows="2"
          :disabled="processing || cardLocked"
          placeholder="e.g., Full balance payment..."
        ></textarea>
      </div>

      <!-- Loading state while we initialize Stripe.js -->
      <div v-if="loading" class="text-center py-3">
        <b-spinner small />
        <span class="ml-2 text-muted">Loading secure card form…</span>
      </div>

      <!-- Error banner -->
      <div v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>

      <!-- Stripe Elements Card mount point -->
      <div class="form-group" v-show="!loading">
        <label>Card Details</label>
        <div ref="cardEl" id="stripe-card-element" class="stripe-card-element"></div>
        <small class="text-muted">Powered by Stripe — your card details never touch Teamio's servers.</small>
      </div>

      <!-- Pay button -->
      <button
        class="btn btn-success btn-block btn-lg mt-3"
        :disabled="!cardReady || processing || amount <= 0 || amount > patientBalance"
        @click="makePayment"
      >
        <span v-if="!processing">Pay ${{ formatAmount(amount) }}</span>
        <span v-else>
          <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
          Processing...
        </span>
      </button>
    </div>

    <!-- Processing overlay -->
    <div v-if="processing" class="processing-overlay">
      <div class="spinner-border text-light" role="status">
        <span class="sr-only">Processing...</span>
      </div>
      <p class="mt-3 text-light">Processing payment...</p>
    </div>
  </b-modal>
</template>

<script>
/* eslint-disable */
import { mapActions } from 'vuex';
import { loadStripe } from '@stripe/stripe-js';

export default {
  name: 'StripePaymentModal',

  props: {
    siteID: { type: String, required: true },
    patNum: { type: [String, Number], required: true },
    patientName: { type: String, required: true },
    patientBalance: { type: Number, required: true },
    patientPhone: { type: String, default: '' },
  },

  data() {
    return {
      visible: false,
      amount: 0,
      note: '',
      loading: false,
      errorMessage: '',
      processing: false,
      paymentSuccess: false,

      // Stripe state
      stripe: null,        // Stripe.js client
      elements: null,      // Elements instance
      cardElement: null,   // Card element
      cardReady: false,    // Card has been mounted and is fillable
      cardLocked: false,   // After confirmCardPayment starts, freeze inputs
      paymentID: null,
      clientSecret: null,
      stripeAccountId: null,
    };
  },

  beforeDestroy() {
    this.destroyCardElement();
  },

  methods: {
    ...mapActions('teamio', ['createStripePaymentIntent']),

    open() {
      this.visible = true;
      this.amount = this.patientBalance;
      this.note = '';
      this.loading = true;
      this.errorMessage = '';
      this.processing = false;
      this.paymentSuccess = false;
      this.cardReady = false;
      this.cardLocked = false;
      this.paymentID = null;
      this.clientSecret = null;
      this.stripeAccountId = null;

      // Defer until DOM is rendered so the mount target exists.
      this.$nextTick(() => this.initStripe());
    },

    /**
     * Two-step Stripe init for Direct charges:
     *   1. Create the PaymentIntent on the connected account (server)
     *   2. Initialize Stripe.js with `stripeAccount` so confirmCardPayment
     *      operates on the connected account.
     */
    async initStripe() {
      try {
        const intentResp = await this.createStripePaymentIntent({
          siteID: this.siteID,
          patNum: this.patNum,
          amount: this.amount,
          patientName: this.patientName,
          patientPhone: this.patientPhone,
          note: this.note,
        });

        if (!intentResp || !intentResp.clientSecret || !intentResp.stripeAccountId) {
          throw new Error('Server did not return a Stripe PaymentIntent');
        }

        this.paymentID = intentResp.paymentID;
        this.clientSecret = intentResp.clientSecret;
        this.stripeAccountId = intentResp.stripeAccountId;

        // Server may return null publishableKey if STRIPE_PUBLISHABLE_KEY is
        // not set in env; fall back to the frontend env var.
        const publishableKey = intentResp.publishableKey || process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY;
        if (!publishableKey) {
          throw new Error('No Stripe publishable key configured');
        }

        this.stripe = await loadStripe(publishableKey, {
          stripeAccount: this.stripeAccountId,
        });
        if (!this.stripe) {
          throw new Error('Failed to load Stripe.js');
        }

        this.elements = this.stripe.elements();
        this.cardElement = this.elements.create('card', {
          style: {
            base: {
              fontSize: '16px',
              color: '#32325d',
              '::placeholder': { color: '#aab7c4' },
            },
            invalid: { color: '#dc3545' },
          },
        });
        this.cardElement.mount(this.$refs.cardEl);

        this.cardElement.on('change', (e) => {
          if (e.error) {
            this.errorMessage = e.error.message;
          } else {
            this.errorMessage = '';
          }
          this.cardReady = e.complete;
        });

        this.loading = false;
      } catch (err) {
        console.error('Error initializing Stripe modal:', err);
        this.errorMessage = err.response?.data?.message || err.message || 'Failed to load Stripe payment form.';
        this.loading = false;
      }
    },

    async makePayment() {
      if (!this.stripe || !this.cardElement || !this.clientSecret) return;
      if (this.amount <= 0) {
        this.errorMessage = 'Amount must be greater than $0.00.';
        return;
      }
      if (this.amount > this.patientBalance) {
        this.errorMessage = `Amount cannot exceed the patient balance of $${this.patientBalance.toFixed(2)}.`;
        return;
      }

      this.errorMessage = '';
      this.processing = true;
      this.cardLocked = true;

      try {
        const result = await this.stripe.confirmCardPayment(this.clientSecret, {
          payment_method: {
            card: this.cardElement,
            billing_details: {
              name: this.patientName,
            },
          },
        });

        if (result.error) {
          this.processing = false;
          this.cardLocked = false;
          this.errorMessage = result.error.message || 'Payment failed. Please try again.';
          return;
        }

        const intent = result.paymentIntent;
        if (intent && (intent.status === 'succeeded' || intent.status === 'processing')) {
          this.paymentSuccess = true;
          this.processing = false;
          // The Connect webhook will mark balance_payments paid via metadata.paymentID;
          // we don't have to call back to the server here.
          setTimeout(() => {
            this.$emit('payment-success', {
              paymentID: this.paymentID,
              amount: this.amount,
              paymentIntentId: intent.id,
            });
          }, 1500);
        } else {
          this.processing = false;
          this.cardLocked = false;
          this.errorMessage = `Unexpected payment status: ${intent?.status}`;
        }
      } catch (err) {
        console.error('Stripe payment error:', err);
        this.processing = false;
        this.cardLocked = false;
        this.errorMessage = err.message || 'Payment failed. Please check your card and try again.';
      }
    },

    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00';
      return parseFloat(amount).toFixed(2);
    },

    close() { this.visible = false; },

    cleanup() {
      this.destroyCardElement();
      this.stripe = null;
      this.elements = null;
      this.cardReady = false;
      this.cardLocked = false;
      this.processing = false;
      this.errorMessage = '';
      this.paymentSuccess = false;
      this.paymentID = null;
      this.clientSecret = null;
      this.stripeAccountId = null;
      this.note = '';
    },

    destroyCardElement() {
      if (this.cardElement) {
        try { this.cardElement.unmount(); } catch (e) { /* ignore */ }
        try { this.cardElement.destroy(); } catch (e) { /* ignore */ }
        this.cardElement = null;
      }
    },
  },
};
</script>

<style scoped>
.stripe-card-element {
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 12px;
  background-color: #fff;
}

.success-checkmark {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #2e7d32;
  color: #ffffff;
  font-size: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.paid-amount {
  font-size: 2rem;
  font-weight: 700;
  color: #2e7d32;
  margin-bottom: 4px;
}

.processing-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  border-radius: 0.3rem;
}
</style>
